/**
 * Item Names — the saved list of box line items (`products`).
 *
 * New names are added automatically whenever staff type one on a shipment, so
 * this screen is mainly for tidying: fixing spellings/HSN, setting a default
 * unit and rate, and removing junk or duplicate entries. Editing or deleting an
 * item here does not change past shipments (each box keeps its own copy).
 */
import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Checkbox from "@mui/material/Checkbox";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNotification } from "context/notification.context";
import { getItemMaster, addItemMaster, updateItemMaster, deleteItemMaster, bulkDeleteItemMaster } from "network/service";
import ItemUpload from "./ItemUpload";

const UOM_OPTIONS = ["PKTS", "NOS", "STRIPS", "BOX", "BOT", "PCS"];
const BLANK = { _id: null, name: "", hsn: "", uom: "", rate: "", manufacturer: "" };

export default function ItemMaster() {
  const { showNotification } = useNotification();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // MUI pagination is 0-based
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // item being added/edited, or null
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Bulk delete: ticked ids (kept across pages), or "everything the search matches".
  const [selected, setSelected] = useState(() => new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = (opts = {}) => {
    const p = opts.page ?? page;
    setLoading(true);
    getItemMaster({ search: opts.search ?? search, page: p + 1, limit: opts.limit ?? limit })
      .then((r) => {
        setRows(r.data.rows || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => showNotification("error", "Error", "Failed to load item names"))
      .finally(() => setLoading(false));
  };

  // Debounced search; any filter change goes back to page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      setSelected(new Set());
      setAllMatching(false);
      load({ page: 0 });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const save = () => {
    const item = editing;
    if (!item.name.trim()) return showNotification("error", "Error", "Item name is required");
    const payload = {
      name: item.name.trim(),
      hsn: (item.hsn || "").trim(),
      uom: item.uom || "",
      rate: item.rate === "" ? 0 : parseFloat(item.rate) || 0,
      manufacturer: (item.manufacturer || "").trim(),
    };
    setSaving(true);
    const req = item._id ? updateItemMaster(item._id, payload) : addItemMaster(payload);
    req
      .then(() => {
        showNotification("success", "Saved", `${payload.name} saved`);
        setEditing(null);
        load();
      })
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Save failed"))
      .finally(() => setSaving(false));
  };

  const remove = () => {
    const item = confirmDelete;
    deleteItemMaster(item._id)
      .then(() => {
        showNotification("success", "Deleted", `${item.name} removed`);
        setConfirmDelete(null);
        load();
      })
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Delete failed"));
  };

  const pageIds = rows.map((r) => r._id);
  const pageAll = rows.length > 0 && (allMatching || pageIds.every((id) => selected.has(id)));
  const pageSome = !pageAll && pageIds.some((id) => selected.has(id));
  const selectedCount = allMatching ? total : selected.size;
  const wholeList = allMatching && !search.trim();

  const togglePage = () => {
    const next = new Set(allMatching ? [] : selected);
    if (pageAll) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    setAllMatching(false);
    setSelected(next);
  };
  const toggleRow = (id) => {
    // Unticking one row while "all matching" is on drops back to this page.
    const next = new Set(allMatching ? pageIds : selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setAllMatching(false);
    setSelected(next);
  };
  const clearSelection = () => {
    setSelected(new Set());
    setAllMatching(false);
  };

  const bulkDelete = () => {
    setBulkDeleting(true);
    bulkDeleteItemMaster(allMatching ? { all_matching: true, search } : { ids: [...selected] })
      .then((r) => {
        showNotification("success", "Deleted", `${r.data.deleted} item name${r.data.deleted === 1 ? "" : "s"} removed`);
        setBulkOpen(false);
        setTyped("");
        clearSelection();
        setPage(0);
        load({ page: 0 });
      })
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Delete failed"))
      .finally(() => setBulkDeleting(false));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={2}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">Item Names</MDTypography>
            <MDBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
              <ItemUpload onDone={() => load()} />
              <MDButton variant="contained" color="white" onClick={() => setEditing({ ...BLANK })}>
                <Icon>add</Icon>&nbsp;Add item
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox px={3} pt={3} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <MDTypography variant="caption" color="text">
              {total} saved item{total === 1 ? "" : "s"} — names are added automatically when typed on a shipment.
              Changes here don&apos;t affect past shipments.
            </MDTypography>
            <MDInput
              placeholder="Search name or HSN"
              value={search}
              sx={{ minWidth: 260 }}
              onChange={(e) => setSearch(e.target.value)}
            />
          </MDBox>

          {selectedCount > 0 && (
            <MDBox mx={3} mt={2} px={2} py={1} display="flex" alignItems="center" gap={1.5} flexWrap="wrap"
              sx={{ background: "#e3f2fd", borderRadius: 1.5 }}>
              <MDTypography variant="button" fontWeight="medium">
                {allMatching
                  ? `All ${total} item${total === 1 ? "" : "s"}${search.trim() ? ` matching "${search.trim()}"` : ""} selected`
                  : `${selected.size} selected`}
              </MDTypography>
              {!allMatching && pageAll && total > selected.size && (
                <MDButton variant="text" color="info" size="small" onClick={() => setAllMatching(true)}>
                  Select all {total}{search.trim() ? ` matching "${search.trim()}"` : " items"}
                </MDButton>
              )}
              <MDButton variant="text" color="secondary" size="small" onClick={clearSelection}>Clear</MDButton>
              <MDBox flexGrow={1} />
              <MDButton variant="gradient" color="error" size="small" onClick={() => { setTyped(""); setBulkOpen(true); }}>
                <Icon>delete</Icon>&nbsp;Delete selected ({selectedCount})
              </MDButton>
            </MDBox>
          )}

          <MDBox px={2} py={1} sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Tooltip title={pageAll ? "Untick this page" : "Tick this page"}>
                      <Checkbox size="small" checked={pageAll} indeterminate={pageSome} onChange={togglePage} disabled={!rows.length} />
                    </Tooltip>
                  </TableCell>
                  {["Name", "HSN", "UOM", "Default rate", "Manufacturer", ""].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: "bold" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row._id} hover selected={allMatching || selected.has(row._id)}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={allMatching || selected.has(row._id)} onChange={() => toggleRow(row._id)} />
                    </TableCell>
                    <TableCell>
                      <MDTypography variant="button" fontWeight="medium">{row.name}</MDTypography>
                    </TableCell>
                    <TableCell>{row.hsn || "—"}</TableCell>
                    <TableCell>{row.uom || "—"}</TableCell>
                    <TableCell>{row.rate ? row.rate : "—"}</TableCell>
                    <TableCell>{row.manufacturer || "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", width: 90 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => setEditing({ ...BLANK, ...row, rate: row.rate ?? "" })}>
                          <Icon fontSize="small">edit</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setConfirmDelete(row)}>
                          <Icon fontSize="small">delete</Icon>
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <MDTypography variant="button" color="text">
                        {search ? "No items match your search." : "No items saved yet."}
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={limit}
              rowsPerPageOptions={[25, 50, 100]}
              onPageChange={(_, p) => {
                setPage(p);
                load({ page: p });
              }}
              onRowsPerPageChange={(e) => {
                const l = parseInt(e.target.value, 10);
                setLimit(l);
                setPage(0);
                load({ page: 0, limit: l });
              }}
            />
          </MDBox>
        </Card>
      </MDBox>

      {/* add / edit */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        {editing && (
          <>
            <DialogTitle>{editing._id ? "Edit item" : "Add item"}</DialogTitle>
            <DialogContent dividers>
              <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
                <MDInput
                  label="Item name"
                  value={editing.name}
                  autoFocus
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
                <MDBox display="flex" gap={2}>
                  <MDInput
                    label="HSN"
                    value={editing.hsn || ""}
                    sx={{ flex: 1 }}
                    onChange={(e) => setEditing({ ...editing, hsn: e.target.value })}
                  />
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel id="item-uom">UOM</InputLabel>
                    <Select
                      labelId="item-uom"
                      label="UOM"
                      value={editing.uom || ""}
                      style={{ height: "1.4375em", padding: 20 }}
                      onChange={(e) => setEditing({ ...editing, uom: e.target.value })}
                    >
                      <MenuItem value="">— none —</MenuItem>
                      {UOM_OPTIONS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                    </Select>
                  </FormControl>
                </MDBox>
                <MDBox display="flex" gap={2}>
                  <MDInput
                    label="Default rate"
                    type="number"
                    inputProps={{ step: "any", min: 0 }}
                    value={editing.rate ?? ""}
                    sx={{ flex: 1 }}
                    onChange={(e) => setEditing({ ...editing, rate: e.target.value })}
                  />
                  <MDInput
                    label="Manufacturer (medicines)"
                    value={editing.manufacturer || ""}
                    sx={{ flex: 1 }}
                    onChange={(e) => setEditing({ ...editing, manufacturer: e.target.value })}
                  />
                </MDBox>
              </MDBox>
            </DialogContent>
            <DialogActions>
              <MDButton variant="text" color="secondary" onClick={() => setEditing(null)}>Cancel</MDButton>
              <MDButton variant="gradient" color="info" disabled={saving} onClick={save}>
                {saving ? "Saving…" : "Save"}
              </MDButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* delete confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete item?</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="button" display="block">
            Remove <b>{confirmDelete?.name}</b> from the saved list?
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Past shipments keep their own copy of this item — only the suggestion list changes.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setConfirmDelete(null)}>Cancel</MDButton>
          <MDButton variant="gradient" color="error" onClick={remove}>Delete</MDButton>
        </DialogActions>
      </Dialog>
      {/* bulk delete confirmation */}
      <Dialog open={bulkOpen} onClose={() => !bulkDeleting && setBulkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {selectedCount} item name{selectedCount === 1 ? "" : "s"}?</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="button" display="block">
            {wholeList
              ? <>This removes <b>every</b> saved item name ({total}).</>
              : allMatching
                ? <>This removes all {total} items matching <b>&quot;{search.trim()}&quot;</b>.</>
                : <>This removes the {selected.size} ticked item{selected.size === 1 ? "" : "s"}.</>}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block" mt={1}>
            Past shipments keep their own copy of each item — only the suggestion list changes. This can&apos;t be undone.
          </MDTypography>
          {wholeList && (
            <MDInput
              label="Type DELETE to confirm"
              value={typed}
              fullWidth
              sx={{ mt: 2 }}
              onChange={(e) => setTyped(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setBulkOpen(false)} disabled={bulkDeleting}>Cancel</MDButton>
          <MDButton variant="gradient" color="error" onClick={bulkDelete} disabled={bulkDeleting || (wholeList && typed.trim() !== "DELETE")}>
            {bulkDeleting ? "Deleting…" : `Delete ${selectedCount}`}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
